/**
 * Laura Learning Dashboard
 *
 * Shows what Laura has learned over time, her anticipations,
 * and traffic predictions. Staff can validate/invalidate
 * patterns to help Laura learn better.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './LauraLearning.css';

interface Memory {
  id: number;
  type: string;
  key: string;
  title: string;
  description: string;
  pattern_data: Record<string, any>;
  confidence: number;
  times_validated: number;
  times_invalidated: number;
  times_used: number;
  created_at: string;
  updated_at: string;
}

interface Anticipation {
  id: number;
  type: string;
  title: string;
  description: string;
  prepared_data: Record<string, any>;
  reasoning: string;
  prepared_for: string | null;
  expires_at: string | null;
  customer_id: number | null;
  customer_name: string | null;
  status: string;
  created_at: string;
}

interface Visitor {
  id: number;
  name: string;
  time_window: string;
  confidence: number;
  predicted_items: string[] | null;
}

interface TrafficDashboard {
  today: {
    expected_visitors: number;
    top_visitors: { name: string; time_window: string; confidence: number }[];
  };
  this_week: {
    total_expected: number;
    by_day: { predicted_date: string; count: number }[];
  };
  partners: {
    restocks_coming: number;
  };
  accuracy: {
    last_30_days: number | null;
    predictions_validated: number;
  };
}

interface LearningDashboard {
  memories: {
    total: number;
    avg_confidence: number;
    by_type: { type: string; count: number; avg_confidence: number }[];
  };
  anticipations: {
    pending: number;
    accepted_30d: number;
    rejected_30d: number;
    accuracy_30d: number | null;
  };
}

interface MemoryType {
  type: string;
  label: string;
  count: number;
}

const memoryTypeLabels: Record<string, string> = {
  customer_pattern: 'Customer Patterns',
  order_pattern: 'Order Patterns',
  production_pattern: 'Production Patterns',
  seasonal_pattern: 'Seasonal Patterns',
  preference: 'Preferences',
  workflow: 'Workflow Patterns',
  supplier_pattern: 'Supplier Patterns',
  correlation: 'Correlations',
};

const anticipationTypeLabels: Record<string, string> = {
  reorder_reminder: 'Reorder Reminder',
  draft_order: 'Draft Order',
  production_suggestion: 'Production Suggestion',
  inventory_alert: 'Inventory Alert',
  customer_outreach: 'Customer Outreach',
};

const LauraLearning: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'memories' | 'anticipations' | 'traffic' | 'rules' | 'intelligence' | 'knowledge'>('overview');
  const [loading, setLoading] = useState(true);

  // Overview data
  const [learningDashboard, setLearningDashboard] = useState<LearningDashboard | null>(null);
  const [trafficDashboard, setTrafficDashboard] = useState<TrafficDashboard | null>(null);

  // Memories tab
  const [memories, setMemories] = useState<Memory[]>([]);
  const [memoryTypes, setMemoryTypes] = useState<MemoryType[]>([]);
  const [selectedMemoryType, setSelectedMemoryType] = useState<string>('all');
  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null);

  // Anticipations tab
  const [anticipations, setAnticipations] = useState<Anticipation[]>([]);
  const [anticipationStatus, setAnticipationStatus] = useState<string>('prepared');

  // Traffic tab
  const [todayVisitors, setTodayVisitors] = useState<Visitor[]>([]);

  // Business Rules tab
  const [businessRules, setBusinessRules] = useState<any[]>([]);
  const [ruleCategories, setRuleCategories] = useState<{value: string; label: string}[]>([]);
  const [selectedRuleCategory, setSelectedRuleCategory] = useState<string>('all');
  const [editingRule, setEditingRule] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [savingRule, setSavingRule] = useState(false);

  // Intelligence tab
  const [millIntel, setMillIntel] = useState<any>(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerIntel, setCustomerIntel] = useState<any>(null);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [customerSearchResults, setCustomerSearchResults] = useState<any[]>([]);

  // Knowledge tab
  const [knowledgeStats, setKnowledgeStats] = useState({ totalArticles: 0, pendingQuestions: 0, documentsUploaded: 0 });
  const [pendingQuestions, setPendingQuestions] = useState<any[]>([]);
  const [topArticles, setTopArticles] = useState<any[]>([]);

  // Actions
  const [runningLearning, setRunningLearning] = useState(false);
  const [runningPredictions, setRunningPredictions] = useState(false);

  useEffect(() => {
    fetchOverviewData();
  }, []);

  useEffect(() => {
    if (activeTab === 'memories') {
      fetchMemories();
      fetchMemoryTypes();
    } else if (activeTab === 'anticipations') {
      fetchAnticipations();
    } else if (activeTab === 'traffic') {
      fetchTrafficData();
    } else if (activeTab === 'rules') {
      fetchBusinessRules();
    } else if (activeTab === 'intelligence') {
      fetchMillIntelligence();
    } else if (activeTab === 'knowledge') {
      fetchKnowledgeData();
    }
  }, [activeTab, selectedMemoryType, anticipationStatus]);

  const fetchOverviewData = async () => {
    setLoading(true);
    try {
      const [learningRes, trafficRes, questionsRes] = await Promise.all([
        api.get('/laura/dashboard/'),
        api.get('/traffic/dashboard/'),
        api.get('/knowledge/questions/pending/').catch(() => ({ data: [] })),
      ]);
      setLearningDashboard(learningRes.data);
      setTrafficDashboard(trafficRes.data);
      setKnowledgeStats(prev => ({ ...prev, pendingQuestions: questionsRes.data.length || 0 }));
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessRules = async () => {
    try {
      const [rulesRes, catRes] = await Promise.all([
        api.get('/laura/business-rules/'),
        api.get('/laura/business-rules/categories/'),
      ]);
      setBusinessRules(rulesRes.data.rules || []);
      setRuleCategories(catRes.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch business rules:', err);
    }
  };

  const fetchMillIntelligence = async () => {
    setIntelLoading(true);
    try {
      const res = await api.get('/admin/ai-intelligence/', { params: { type: 'mill' } });
      setMillIntel(res.data);
    } catch (err) {
      console.error('Failed to fetch mill intelligence:', err);
    } finally {
      setIntelLoading(false);
    }
  };

  const fetchKnowledgeData = async () => {
    try {
      const [articlesRes, questionsRes, docsRes] = await Promise.all([
        api.get('/knowledge/articles/'),
        api.get('/knowledge/questions/pending/'),
        api.get('/knowledge/documents/'),
      ]);
      setKnowledgeStats({
        totalArticles: articlesRes.data.length || articlesRes.data.count || 0,
        pendingQuestions: questionsRes.data.length || 0,
        documentsUploaded: docsRes.data.length || docsRes.data.count || 0,
      });
      setPendingQuestions((questionsRes.data || []).slice(0, 5));
      const sorted = [...(articlesRes.data.results || articlesRes.data)]
        .sort((a: any, b: any) => (b.times_used || 0) - (a.times_used || 0));
      setTopArticles(sorted.slice(0, 5));
    } catch (err) {
      console.error('Failed to fetch knowledge data:', err);
    }
  };

  const searchCustomerIntel = async () => {
    if (!customerSearch.trim()) return;
    setCustomerSearchLoading(true);
    setCustomerIntel(null);
    try {
      // Search customers by name
      const searchRes = await api.get('/customers/', { params: { search: customerSearch, limit: 5 } });
      const customers = searchRes.data?.results || searchRes.data || [];
      setCustomerSearchResults(customers);
      if (customers.length === 1) {
        loadCustomerIntel(customers[0].id, customers[0].customer_type === 'RETAIL_PARTNER' ? 'partner' : 'customer');
      }
    } catch (err) {
      console.error('Customer search failed:', err);
      setCustomerSearchResults([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const loadCustomerIntel = async (customerId: number, type: string = 'customer') => {
    setCustomerSearchLoading(true);
    try {
      const res = await api.get('/admin/ai-intelligence/', { params: { type, customer_id: customerId } });
      setCustomerIntel(res.data);
    } catch (err) {
      console.error('Failed to load customer intelligence:', err);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const handleSaveRule = async (ruleId: number) => {
    setSavingRule(true);
    try {
      await api.patch(`/laura/business-rules/${ruleId}/`, { value: editValue });
      setEditingRule(null);
      fetchBusinessRules();
    } catch (err) {
      console.error('Failed to save rule:', err);
    } finally {
      setSavingRule(false);
    }
  };

  const fetchMemories = async () => {
    try {
      const params: Record<string, string> = {};
      if (selectedMemoryType !== 'all') {
        params.type = selectedMemoryType;
      }
      const response = await api.get('/laura/memories/', { params });
      setMemories(response.data);
    } catch (err) {
      console.error('Failed to fetch memories:', err);
    }
  };

  const fetchMemoryTypes = async () => {
    try {
      const response = await api.get('/laura/memories/types/');
      setMemoryTypes(response.data);
    } catch (err) {
      console.error('Failed to fetch memory types:', err);
    }
  };

  const fetchAnticipations = async () => {
    try {
      const response = await api.get('/laura/anticipations/', {
        params: { status: anticipationStatus }
      });
      setAnticipations(response.data);
    } catch (err) {
      console.error('Failed to fetch anticipations:', err);
    }
  };

  const fetchTrafficData = async () => {
    try {
      const response = await api.get('/traffic/today/');
      setTodayVisitors(response.data);
    } catch (err) {
      console.error('Failed to fetch traffic data:', err);
    }
  };

  const handleValidateMemory = async (memoryId: number) => {
    try {
      await api.post(`/laura/memories/${memoryId}/validate/`);
      fetchMemories();
      if (selectedMemory?.id === memoryId) {
        const response = await api.get(`/laura/memories/${memoryId}/`);
        setSelectedMemory(response.data);
      }
    } catch (err) {
      console.error('Failed to validate memory:', err);
    }
  };

  const handleInvalidateMemory = async (memoryId: number) => {
    try {
      await api.post(`/laura/memories/${memoryId}/invalidate/`);
      fetchMemories();
      if (selectedMemory?.id === memoryId) {
        const response = await api.get(`/laura/memories/${memoryId}/`);
        setSelectedMemory(response.data);
      }
    } catch (err) {
      console.error('Failed to invalidate memory:', err);
    }
  };

  const handleAnticipationFeedback = async (anticipationId: number, wasHelpful: boolean) => {
    try {
      await api.post(`/laura/anticipations/${anticipationId}/feedback/`, {
        was_helpful: wasHelpful
      });
      fetchAnticipations();
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    }
  };

  const handleRunLearning = async () => {
    setRunningLearning(true);
    try {
      const response = await api.post('/laura/run-learning/');
      alert(`Learning complete! Found ${response.data.total_patterns} patterns.`);
      fetchOverviewData();
      if (activeTab === 'memories') {
        fetchMemories();
      }
    } catch (err) {
      console.error('Failed to run learning:', err);
      alert('Failed to run learning');
    } finally {
      setRunningLearning(false);
    }
  };

  const handleRunPredictions = async () => {
    setRunningPredictions(true);
    try {
      const response = await api.post('/traffic/generate-predictions/');
      alert(`Predictions generated! ${response.data.visit_predictions} visitor predictions created.`);
      fetchOverviewData();
      if (activeTab === 'traffic') {
        fetchTrafficData();
      }
    } catch (err) {
      console.error('Failed to run predictions:', err);
      alert('Failed to generate predictions');
    } finally {
      setRunningPredictions(false);
    }
  };

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 80) return '#10b981';
    if (confidence >= 60) return '#f59e0b';
    if (confidence >= 40) return '#6b7280';
    return '#ef4444';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const renderOverview = () => (
    <div className="laura-overview">
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon memories-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z"/>
              <path d="M12 6v6l4 2"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{learningDashboard?.memories.total || 0}</span>
            <span className="stat-label">Patterns Learned</span>
          </div>
          {learningDashboard?.memories.avg_confidence && (
            <div className="stat-badge" style={{ backgroundColor: getConfidenceColor(learningDashboard.memories.avg_confidence) }}>
              {Math.round(learningDashboard.memories.avg_confidence)}% avg confidence
            </div>
          )}
        </div>

        <div className="stat-card">
          <div className="stat-icon anticipations-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{learningDashboard?.anticipations.pending || 0}</span>
            <span className="stat-label">Prepared Items</span>
          </div>
          {learningDashboard?.anticipations.accuracy_30d && (
            <div className="stat-badge success">
              {Math.round(learningDashboard.anticipations.accuracy_30d)}% accurate (30d)
            </div>
          )}
        </div>

        <div className="stat-card">
          <div className="stat-icon traffic-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{trafficDashboard?.today.expected_visitors || 0}</span>
            <span className="stat-label">Expected Today</span>
          </div>
          {trafficDashboard?.accuracy.last_30_days && (
            <div className="stat-badge success">
              {Math.round(trafficDashboard.accuracy.last_30_days)}% prediction accuracy
            </div>
          )}
        </div>

        <div className="stat-card">
          <div className="stat-icon partners-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{trafficDashboard?.partners.restocks_coming || 0}</span>
            <span className="stat-label">Partner Restocks Coming</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="action-buttons">
          <button
            className="action-btn primary"
            onClick={handleRunLearning}
            disabled={runningLearning}
          >
            {runningLearning ? (
              <>
                <span className="spinner" />
                Learning...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
                Run Learning Now
              </>
            )}
          </button>
          <button
            className="action-btn secondary"
            onClick={handleRunPredictions}
            disabled={runningPredictions}
          >
            {runningPredictions ? (
              <>
                <span className="spinner" />
                Generating...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                </svg>
                Generate Predictions
              </>
            )}
          </button>
        </div>
      </div>

      {/* Memory Types Breakdown */}
      {learningDashboard?.memories.by_type && learningDashboard.memories.by_type.length > 0 && (
        <div className="memory-breakdown">
          <h3>What Laura Has Learned</h3>
          <div className="breakdown-grid">
            {learningDashboard.memories.by_type.map((item) => (
              <div
                key={item.type}
                className="breakdown-item"
                onClick={() => {
                  setSelectedMemoryType(item.type);
                  setActiveTab('memories');
                }}
              >
                <div className="breakdown-header">
                  <span className="breakdown-label">{memoryTypeLabels[item.type] || item.type}</span>
                  <span className="breakdown-count">{item.count}</span>
                </div>
                <div className="confidence-bar">
                  <div
                    className="confidence-fill"
                    style={{
                      width: `${item.avg_confidence}%`,
                      backgroundColor: getConfidenceColor(item.avg_confidence)
                    }}
                  />
                </div>
                <span className="confidence-label">{Math.round(item.avg_confidence)}% confidence</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today's Expected Visitors */}
      {trafficDashboard?.today.top_visitors && trafficDashboard.today.top_visitors.length > 0 && (
        <div className="today-visitors">
          <h3>Expected Today</h3>
          <div className="visitors-list">
            {trafficDashboard.today.top_visitors.map((visitor, index) => (
              <div key={index} className="visitor-item">
                <div className="visitor-avatar">
                  {visitor.name.charAt(0).toUpperCase()}
                </div>
                <div className="visitor-info">
                  <span className="visitor-name">{visitor.name}</span>
                  <span className="visitor-time">{visitor.time_window || 'Sometime today'}</span>
                </div>
                <div
                  className="visitor-confidence"
                  style={{ color: getConfidenceColor(visitor.confidence) }}
                >
                  {Math.round(visitor.confidence)}%
                </div>
              </div>
            ))}
          </div>
          <button className="see-all-btn" onClick={() => setActiveTab('traffic')}>
            See All Traffic Predictions
          </button>
        </div>
      )}
    </div>
  );

  const renderMemories = () => (
    <div className="memories-tab">
      {/* Filters */}
      <div className="tab-filters">
        <select
          value={selectedMemoryType}
          onChange={(e) => setSelectedMemoryType(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Types</option>
          {memoryTypes.map((type) => (
            <option key={type.type} value={type.type}>
              {type.label} ({type.count})
            </option>
          ))}
        </select>
      </div>

      <div className="memories-layout">
        {/* Memory List */}
        <div className="memories-list">
          {memories.length === 0 ? (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2z"/>
                <path d="M12 6v6l4 2"/>
              </svg>
              <p>No patterns learned yet</p>
              <button className="action-btn primary" onClick={handleRunLearning}>
                Run Learning
              </button>
            </div>
          ) : (
            memories.map((memory) => (
              <div
                key={memory.id}
                className={`memory-card ${selectedMemory?.id === memory.id ? 'selected' : ''}`}
                onClick={() => setSelectedMemory(memory)}
              >
                <div className="memory-header">
                  <span className="memory-type">{memoryTypeLabels[memory.type] || memory.type}</span>
                  <span
                    className="memory-confidence"
                    style={{ backgroundColor: getConfidenceColor(memory.confidence) }}
                  >
                    {Math.round(memory.confidence)}%
                  </span>
                </div>
                <h4 className="memory-title">{memory.title}</h4>
                <p className="memory-description">{memory.description}</p>
                <div className="memory-meta">
                  <span>Used {memory.times_used}x</span>
                  <span>Updated {formatDate(memory.updated_at)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Memory Detail */}
        {selectedMemory && (
          <div className="memory-detail">
            <div className="detail-header">
              <h3>{selectedMemory.title}</h3>
              <button className="close-btn" onClick={() => setSelectedMemory(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="detail-content">
              <div className="detail-section">
                <label>Type</label>
                <span>{memoryTypeLabels[selectedMemory.type] || selectedMemory.type}</span>
              </div>

              <div className="detail-section">
                <label>Description</label>
                <p>{selectedMemory.description}</p>
              </div>

              <div className="detail-section">
                <label>Confidence</label>
                <div className="confidence-display">
                  <div className="confidence-bar large">
                    <div
                      className="confidence-fill"
                      style={{
                        width: `${selectedMemory.confidence}%`,
                        backgroundColor: getConfidenceColor(selectedMemory.confidence)
                      }}
                    />
                  </div>
                  <span>{Math.round(selectedMemory.confidence)}%</span>
                </div>
              </div>

              <div className="detail-stats">
                <div className="stat">
                  <span className="stat-num">{selectedMemory.times_validated}</span>
                  <span className="stat-label">Validated</span>
                </div>
                <div className="stat">
                  <span className="stat-num">{selectedMemory.times_invalidated}</span>
                  <span className="stat-label">Invalidated</span>
                </div>
                <div className="stat">
                  <span className="stat-num">{selectedMemory.times_used}</span>
                  <span className="stat-label">Times Used</span>
                </div>
              </div>

              {selectedMemory.pattern_data && (
                <div className="detail-section">
                  <label>Pattern Data</label>
                  <pre className="pattern-data">
                    {JSON.stringify(selectedMemory.pattern_data, null, 2)}
                  </pre>
                </div>
              )}

              <div className="detail-actions">
                <p className="action-prompt">Was this pattern accurate?</p>
                <div className="action-buttons">
                  <button
                    className="action-btn success"
                    onClick={() => handleValidateMemory(selectedMemory.id)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                    Yes, Validate
                  </button>
                  <button
                    className="action-btn danger"
                    onClick={() => handleInvalidateMemory(selectedMemory.id)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                    No, Invalidate
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderAnticipations = () => (
    <div className="anticipations-tab">
      {/* Status Filter */}
      <div className="tab-filters">
        <div className="status-tabs">
          {['prepared', 'presented', 'accepted', 'rejected', 'expired'].map((status) => (
            <button
              key={status}
              className={`status-tab ${anticipationStatus === status ? 'active' : ''}`}
              onClick={() => setAnticipationStatus(status)}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="anticipations-list">
        {anticipations.length === 0 ? (
          <div className="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 18l6-6-6-6"/>
            </svg>
            <p>No {anticipationStatus} anticipations</p>
          </div>
        ) : (
          anticipations.map((anticipation) => (
            <div key={anticipation.id} className="anticipation-card">
              <div className="anticipation-header">
                <span className="anticipation-type">
                  {anticipationTypeLabels[anticipation.type] || anticipation.type}
                </span>
                <span className={`anticipation-status status-${anticipation.status}`}>
                  {anticipation.status}
                </span>
              </div>
              <h4 className="anticipation-title">{anticipation.title}</h4>
              <p className="anticipation-description">{anticipation.description}</p>

              {anticipation.customer_name && (
                <div className="anticipation-customer">
                  For: {anticipation.customer_name}
                </div>
              )}

              {anticipation.reasoning && (
                <div className="anticipation-reasoning">
                  <strong>Why:</strong> {anticipation.reasoning}
                </div>
              )}

              <div className="anticipation-meta">
                {anticipation.prepared_for && (
                  <span>Prepared for: {formatDate(anticipation.prepared_for)}</span>
                )}
                <span>Created: {formatDate(anticipation.created_at)}</span>
              </div>

              {anticipation.status === 'prepared' && (
                <div className="anticipation-actions">
                  <button
                    className="action-btn success small"
                    onClick={() => handleAnticipationFeedback(anticipation.id, true)}
                  >
                    Helpful
                  </button>
                  <button
                    className="action-btn danger small"
                    onClick={() => handleAnticipationFeedback(anticipation.id, false)}
                  >
                    Not Helpful
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderTraffic = () => (
    <div className="traffic-tab">
      {/* Traffic Stats */}
      <div className="traffic-stats">
        <div className="traffic-stat-card">
          <h4>Today</h4>
          <span className="big-number">{trafficDashboard?.today.expected_visitors || 0}</span>
          <span className="stat-label">expected visitors</span>
        </div>
        <div className="traffic-stat-card">
          <h4>This Week</h4>
          <span className="big-number">{trafficDashboard?.this_week.total_expected || 0}</span>
          <span className="stat-label">total expected</span>
        </div>
        <div className="traffic-stat-card">
          <h4>Prediction Accuracy</h4>
          <span className="big-number">
            {trafficDashboard?.accuracy.last_30_days
              ? `${Math.round(trafficDashboard.accuracy.last_30_days)}%`
              : 'N/A'}
          </span>
          <span className="stat-label">last 30 days</span>
        </div>
      </div>

      {/* Today's Visitors */}
      <div className="traffic-section">
        <h3>Expected Today</h3>
        {todayVisitors.length === 0 ? (
          <div className="empty-state small">
            <p>No visitor predictions for today</p>
          </div>
        ) : (
          <div className="visitors-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Expected Time</th>
                  <th>Predicted Items</th>
                  <th>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {todayVisitors.map((visitor) => (
                  <tr key={visitor.id}>
                    <td>
                      <div className="visitor-cell">
                        <div className="visitor-avatar small">
                          {visitor.name.charAt(0).toUpperCase()}
                        </div>
                        {visitor.name}
                      </div>
                    </td>
                    <td>{visitor.time_window || 'Anytime'}</td>
                    <td>
                      {visitor.predicted_items?.slice(0, 2).join(', ') || '-'}
                    </td>
                    <td>
                      <span
                        className="confidence-badge"
                        style={{ backgroundColor: getConfidenceColor(visitor.confidence) }}
                      >
                        {Math.round(visitor.confidence)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Weekly Breakdown */}
      {trafficDashboard?.this_week.by_day && trafficDashboard.this_week.by_day.length > 0 && (
        <div className="traffic-section">
          <h3>This Week</h3>
          <div className="weekly-chart">
            {trafficDashboard.this_week.by_day.map((day) => {
              const date = new Date(day.predicted_date);
              const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
              const maxCount = Math.max(...trafficDashboard.this_week.by_day.map(d => d.count));
              const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;

              return (
                <div key={day.predicted_date} className="chart-bar-wrapper">
                  <div className="chart-bar" style={{ height: `${height}%` }}>
                    <span className="bar-value">{day.count}</span>
                  </div>
                  <span className="bar-label">{dayName}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const renderKnowledge = () => (
    <div className="knowledge-tab">
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="section-card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#d97706' }}>{knowledgeStats.pendingQuestions}</div>
          <div style={{ color: '#666', fontSize: '0.85rem' }}>Pending Questions</div>
          <a href="/knowledge/pending" style={{ fontSize: '0.8rem', color: '#2D5A27' }}>Review now</a>
        </div>
        <div className="section-card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#2D5A27' }}>{knowledgeStats.totalArticles}</div>
          <div style={{ color: '#666', fontSize: '0.85rem' }}>Knowledge Articles</div>
          <a href="/knowledge/articles" style={{ fontSize: '0.8rem', color: '#2D5A27' }}>Browse</a>
        </div>
        <div className="section-card" style={{ textAlign: 'center', padding: '1.25rem' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#3b82f6' }}>{knowledgeStats.documentsUploaded}</div>
          <div style={{ color: '#666', fontSize: '0.85rem' }}>Documents</div>
          <a href="/knowledge/documents" style={{ fontSize: '0.8rem', color: '#2D5A27' }}>Manage</a>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {/* Pending Questions */}
        <div className="section-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0 }}>Needs Answers</h3>
            <a href="/knowledge/pending" style={{ fontSize: '0.8rem', color: '#2D5A27' }}>View all</a>
          </div>
          {pendingQuestions.length === 0 ? (
            <p style={{ color: '#999', textAlign: 'center', padding: '1rem 0' }}>All caught up!</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {pendingQuestions.map((q: any) => (
                <li key={q.id} style={{ padding: '0.5rem 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{q.question}</div>
                  <div style={{ color: '#999', fontSize: '0.75rem' }}>{q.customer_name || 'Unknown'} &bull; {new Date(q.created_at).toLocaleDateString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Top Articles */}
        <div className="section-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <h3 style={{ margin: 0 }}>Most Used Articles</h3>
            <a href="/knowledge/articles" style={{ fontSize: '0.8rem', color: '#2D5A27' }}>View all</a>
          </div>
          {topArticles.length === 0 ? (
            <p style={{ color: '#999', textAlign: 'center', padding: '1rem 0' }}>No articles yet</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {topArticles.map((a: any) => (
                <li key={a.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span style={{ fontSize: '0.85rem' }}>{a.question}</span>
                  <span style={{ color: '#888', fontSize: '0.8rem', whiteSpace: 'nowrap', marginLeft: '0.5rem' }}>{a.times_used} uses</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <a href="/knowledge/articles/new" style={{ padding: '0.5rem 1rem', background: '#2D5A27', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.85rem' }}>
          + Add New Article
        </a>
        <a href="/knowledge/documents" style={{ padding: '0.5rem 1rem', background: '#3b82f6', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '0.85rem' }}>
          Upload Document
        </a>
      </div>
    </div>
  );

  const renderIntelligence = () => {
    return (
      <div className="intelligence-tab">
        {/* Mill Intelligence */}
        <div className="section-card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12 }}>Mill Intelligence (Next 30 Days)</h3>
          {intelLoading ? (
            <div className="loading-state"><div className="spinner" /> Loading...</div>
          ) : millIntel ? (
            <div>
              {/* Upcoming Farmhand Orders */}
              {millIntel.upcoming_farmhand_orders?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Farmhand Orders ({millIntel.upcoming_farmhand_orders.length})</h4>
                  <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                        <th style={{ padding: '4px 8px' }}>Ship Date</th>
                        <th style={{ padding: '4px 8px' }}>Customer</th>
                        <th style={{ padding: '4px 8px', textAlign: 'right' }}>Lbs</th>
                        <th style={{ padding: '4px 8px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {millIntel.upcoming_farmhand_orders.map((o: any, i: number) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '4px 8px' }}>{o.ship_date}</td>
                          <td style={{ padding: '4px 8px' }}>{o.customer}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>{Number(o.total_lbs).toLocaleString()}</td>
                          <td style={{ padding: '4px 8px' }}>{o.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Partner Restocks */}
              {millIntel.partner_restocks?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Partner Restocks ({millIntel.partner_restocks.length})</h4>
                  <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                        <th style={{ padding: '4px 8px' }}>Restock By</th>
                        <th style={{ padding: '4px 8px' }}>Partner</th>
                        <th style={{ padding: '4px 8px', textAlign: 'right' }}>Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {millIntel.partner_restocks.map((p: any, i: number) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '4px 8px' }}>{p.restock_date}</td>
                          <td style={{ padding: '4px 8px' }}>{p.partner}</td>
                          <td style={{ padding: '4px 8px', textAlign: 'right' }}>{p.confidence}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Low Bins */}
              {millIntel.low_bins?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: '#dc2626' }}>Low Bins ({millIntel.low_bins.length})</h4>
                  {millIntel.low_bins.map((b: any, i: number) => (
                    <div key={i} style={{ fontSize: 13, padding: '2px 0' }}>
                      <strong>{b.bin_name}</strong>: {b.product} — {Number(b.current_lbs).toLocaleString()}/{Number(b.capacity_lbs).toLocaleString()} lbs ({b.fill_pct}%)
                    </div>
                  ))}
                </div>
              )}

              {/* Laura's Insights */}
              {millIntel.laura_insights?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Laura's Insights</h4>
                  {millIntel.laura_insights.map((ins: any, i: number) => (
                    <div key={i} style={{ fontSize: 13, padding: '4px 0', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ fontWeight: 600, textTransform: 'uppercase', fontSize: 11, color: '#6b7280', marginRight: 8 }}>{ins.category}</span>
                      {ins.title}
                    </div>
                  ))}
                </div>
              )}

              {/* Volume */}
              {millIntel.recent_volume?.total_orders > 0 && (
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 12 }}>
                  Last 30 days: {millIntel.recent_volume.total_orders} orders, {Number(millIntel.recent_volume.total_lbs).toLocaleString()} lbs
                </div>
              )}

              {!millIntel.upcoming_farmhand_orders?.length && !millIntel.partner_restocks?.length && !millIntel.low_bins?.length && (
                <p style={{ color: '#6b7280', fontSize: 13 }}>No upcoming orders or alerts in the next 30 days.</p>
              )}
            </div>
          ) : (
            <p style={{ color: '#6b7280' }}>No mill intelligence data available.</p>
          )}
        </div>

        {/* Customer Lookup */}
        <div className="section-card">
          <h3 style={{ marginBottom: 12 }}>Customer Intelligence Lookup</h3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              type="text"
              value={customerSearch}
              onChange={e => setCustomerSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchCustomerIntel()}
              placeholder="Search by customer name..."
              style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
            />
            <button
              className="btn btn-primary"
              onClick={searchCustomerIntel}
              disabled={customerSearchLoading || !customerSearch.trim()}
              style={{ padding: '8px 16px' }}
            >
              {customerSearchLoading ? 'Searching...' : 'Look Up'}
            </button>
          </div>

          {/* Search results picker */}
          {customerSearchResults.length > 1 && !customerIntel && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>Multiple matches — select one:</p>
              {customerSearchResults.map((c: any) => (
                <button
                  key={c.id}
                  onClick={() => loadCustomerIntel(c.id, c.customer_type === 'RETAIL_PARTNER' ? 'partner' : 'customer')}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', marginBottom: 4, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}
                >
                  <strong>{c.name}</strong> — {c.contact_person} ({c.customer_type})
                </button>
              ))}
            </div>
          )}

          {/* Intelligence card */}
          {customerIntel && (
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: 16, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 }}>
              {customerIntel.summary_text || 'No intelligence data available for this customer.'}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderBusinessRules = () => {
    const catLabel = (cat: string) => {
      const found = ruleCategories.find(c => c.value === cat);
      return found ? found.label : cat;
    };

    const filtered = selectedRuleCategory === 'all'
      ? businessRules
      : businessRules.filter(r => r.category === selectedRuleCategory);

    // Group by category
    const grouped: Record<string, any[]> = {};
    for (const rule of filtered) {
      if (!grouped[rule.category]) grouped[rule.category] = [];
      grouped[rule.category].push(rule);
    }

    return (
      <div className="business-rules-tab">
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ fontWeight: 600, fontSize: 14 }}>Category:</label>
          <select
            value={selectedRuleCategory}
            onChange={e => setSelectedRuleCategory(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14 }}
          >
            <option value="all">All Categories</option>
            {ruleCategories.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <span style={{ color: '#6b7280', fontSize: 13 }}>
            {filtered.length} rule{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {Object.entries(grouped).map(([cat, rules]) => (
          <div key={cat} style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#374151', marginBottom: 8,
              borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              {catLabel(cat)}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {rules.map((rule: any) => (
                <div key={rule.id} style={{
                  background: rule.is_active ? '#fff' : '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: '10px 14px',
                  opacity: rule.is_active ? 1 : 0.6,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <code style={{ fontSize: 12, color: '#6C8062', background: '#f0fdf4',
                          padding: '1px 6px', borderRadius: 4 }}>{rule.key}</code>
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>{rule.value_type}</span>
                      </div>
                      {rule.description && (
                        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{rule.description}</div>
                      )}
                      {editingRule === rule.id ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 4 }}>
                          {rule.value_type === 'text' || rule.value_type === 'json' ? (
                            <textarea
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              rows={6}
                              style={{ flex: 1, padding: 8, fontSize: 13, border: '1px solid #d1d5db',
                                borderRadius: 6, fontFamily: 'monospace', resize: 'vertical' }}
                            />
                          ) : (
                            <input
                              type="text"
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              style={{ flex: 1, padding: '6px 10px', fontSize: 13, border: '1px solid #d1d5db',
                                borderRadius: 6, fontFamily: 'monospace' }}
                            />
                          )}
                          <button
                            onClick={() => handleSaveRule(rule.id)}
                            disabled={savingRule}
                            style={{ padding: '6px 14px', fontSize: 13, fontWeight: 600,
                              background: '#6C8062', color: '#fff', border: 'none', borderRadius: 6,
                              cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            {savingRule ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingRule(null)}
                            style={{ padding: '6px 14px', fontSize: 13, background: '#f3f4f6',
                              border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() => { setEditingRule(rule.id); setEditValue(rule.value); }}
                          style={{
                            fontSize: 13,
                            fontFamily: 'monospace',
                            color: '#111827',
                            background: '#f9fafb',
                            padding: '4px 8px',
                            borderRadius: 4,
                            cursor: 'pointer',
                            maxHeight: 80,
                            overflow: 'hidden',
                            whiteSpace: rule.value_type === 'text' ? 'pre-wrap' : 'nowrap',
                            textOverflow: 'ellipsis',
                          }}
                          title="Click to edit"
                        >
                          {rule.value.length > 200 ? rule.value.slice(0, 200) + '...' : rule.value}
                        </div>
                      )}
                    </div>
                  </div>
                  {rule.updated_by && (
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                      Updated by {rule.updated_by} {rule.updated_at ? `on ${new Date(rule.updated_at).toLocaleDateString()}` : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>
            <p>No business rules found. Run the seed command to populate.</p>
            <code style={{ fontSize: 12 }}>python manage.py seed_ai_rules</code>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="laura-learning-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <img src="/images/laura.png" alt="Laura" />
          </div>
          <div className="header-text">
            <h1>Laura's Brain</h1>
            <p>Business rules, knowledge, learning patterns, and intelligence</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="learning-tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'memories' ? 'active' : ''}`}
          onClick={() => setActiveTab('memories')}
        >
          Memories
          {learningDashboard?.memories.total ? (
            <span className="tab-badge">{learningDashboard.memories.total}</span>
          ) : null}
        </button>
        <button
          className={`tab ${activeTab === 'anticipations' ? 'active' : ''}`}
          onClick={() => setActiveTab('anticipations')}
        >
          Anticipations
          {learningDashboard?.anticipations.pending ? (
            <span className="tab-badge">{learningDashboard.anticipations.pending}</span>
          ) : null}
        </button>
        <button
          className={`tab ${activeTab === 'traffic' ? 'active' : ''}`}
          onClick={() => setActiveTab('traffic')}
        >
          Traffic
          {trafficDashboard?.today.expected_visitors ? (
            <span className="tab-badge">{trafficDashboard.today.expected_visitors}</span>
          ) : null}
        </button>
        <button
          className={`tab ${activeTab === 'rules' ? 'active' : ''}`}
          onClick={() => setActiveTab('rules')}
        >
          Business Rules
        </button>
        <button
          className={`tab ${activeTab === 'knowledge' ? 'active' : ''}`}
          onClick={() => setActiveTab('knowledge')}
        >
          Knowledge
          {knowledgeStats.pendingQuestions > 0 && (
            <span className="tab-badge">{knowledgeStats.pendingQuestions}</span>
          )}
        </button>
        <button
          className={`tab ${activeTab === 'intelligence' ? 'active' : ''}`}
          onClick={() => setActiveTab('intelligence')}
        >
          Intelligence
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner large" />
            <p>Loading Laura's brain...</p>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'memories' && renderMemories()}
            {activeTab === 'anticipations' && renderAnticipations()}
            {activeTab === 'traffic' && renderTraffic()}
            {activeTab === 'rules' && renderBusinessRules()}
            {activeTab === 'knowledge' && renderKnowledge()}
            {activeTab === 'intelligence' && renderIntelligence()}
          </>
        )}
      </div>
    </div>
  );
};

export default LauraLearning;
